import { Virtuoso } from 'react-virtuoso';

import type { LiveTranscriptSegment } from '../hooks/useSessionTranscript';
import type { EffectiveClass } from '../lib/effectiveClass';
import type { SessionParticipantNames } from '../lib/participantDisplayName';

export type TranscriptBodySegment = Pick<
  LiveTranscriptSegment,
  'id' | 'text' | 'participantIdentity' | 'participantName'
>;

interface Props {
  segments: TranscriptBodySegment[];
  /** True iff a LiveKit AGENT-kind participant is currently in the room. */
  botPresent: boolean;
  /** From session.clientId — for speaker labeling. */
  clientUserId: string | null | undefined;
  /** From session.practitionerId — for speaker labeling. */
  practitionerUserId: string | null | undefined;
  /** Local viewer's role. The viewer's own speech is labeled "You"; the
   *  opposite party is labeled with their session name (or a role-class
   *  fallback). Observers see both parties' names, never "You". */
  viewerClass: EffectiveClass;
  /** Display names from the session record. Used to label the non-viewer
   *  speaker so the practitioner sees "Alex" instead of generic "Client",
   *  and the client sees "Dr Smith" instead of generic "Practitioner". */
  sessionNames: SessionParticipantNames;
}

type SpeakerRole = 'client' | 'practitioner' | 'other';

function speakerRoleFor(
  identity: string,
  clientUserId: string | null | undefined,
  practitionerUserId: string | null | undefined,
): SpeakerRole {
  if (clientUserId && identity === clientUserId) return 'client';
  if (practitionerUserId && identity === practitionerUserId) return 'practitioner';
  return 'other';
}

function speakerLabel(
  seg: TranscriptBodySegment,
  role: SpeakerRole,
  viewerClass: EffectiveClass,
  sessionNames: SessionParticipantNames,
): string {
  // The viewer's own speech.
  if (role === 'client' && viewerClass === 'client') return 'You';
  if (role === 'practitioner' && viewerClass === 'practitioner') return 'You';
  // Opposite party — surface the actual name so each side recognises who's
  // talking. Falls back to the generic role label when the session record
  // has no name (e.g. anonymous helpline sessions).
  if (role === 'client') {
    const name = sessionNames.clientName?.trim();
    return name && name.length > 0 ? name : 'Client';
  }
  if (role === 'practitioner') {
    const name = sessionNames.practitionerName?.trim();
    return name && name.length > 0 ? name : 'Practitioner';
  }
  // Unknown speaker (observer joining, transcription bot misfire, etc.) —
  // use the LiveKit-provided name if there is one.
  return seg.participantName || 'Speaker';
}

interface TranscriptRowContext {
  clientUserId: string | null | undefined;
  practitionerUserId: string | null | undefined;
  viewerClass: EffectiveClass;
  sessionNames: SessionParticipantNames;
}

/**
 * Row renderer kept at module scope so React's reconciler sees a stable
 * component type across renders (lint: react/no-unstable-nested-components).
 * The per-instance lookup ids flow in via Virtuoso's `context` prop.
 */
function renderTranscriptRow(
  _index: number,
  seg: TranscriptBodySegment,
  ctx: TranscriptRowContext,
) {
  const role = speakerRoleFor(seg.participantIdentity, ctx.clientUserId, ctx.practitionerUserId);
  const label = speakerLabel(seg, role, ctx.viewerClass, ctx.sessionNames);
  return (
    <div className="border-b border-white/4 px-5 py-3" data-speaker-role={role}>
      <div className="text-[11px] tracking-wide text-white/45 uppercase">{label}</div>
      <div className="mt-1 text-[14px] leading-snug text-white/90">{seg.text}</div>
    </div>
  );
}

/**
 * Body-only transcript renderer. The drawer chrome (header, status copy,
 * sizing/positioning) lives in the parent `SidePanel` shell — this component
 * is just the segment list (or its empty state).
 *
 * Virtualized for long sessions (300–800 segments per the spec). States
 * covered: waiting-for-bot, listening, transcribing. The bot-left-mid-call
 * status copy belongs to the parent shell.
 *
 * Mirrored to both parties — the client sees the full transcript too, by
 * product decision. Speaker labels are perspective-aware so each side reads
 * "You" for their own speech and the other party's actual name for theirs.
 */
export function TranscriptBody({
  segments,
  botPresent,
  clientUserId,
  practitionerUserId,
  viewerClass,
  sessionNames,
}: Props) {
  if (segments.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-5 text-[13px] text-white/55">
        {botPresent ? 'Listening…' : 'Waiting for live transcription…'}
      </div>
    );
  }
  return (
    <Virtuoso<TranscriptBodySegment, TranscriptRowContext>
      data={segments}
      followOutput="smooth"
      className="flex-1"
      // SSR/jsdom escape hatch: when the container has zero measured size
      // (test envs, server render), Virtuoso would render zero rows. Capping
      // the initial slice means tests see real DOM and SSR ships meaningful
      // markup; in the browser Virtuoso re-measures after mount and resumes
      // normal virtualization.
      initialItemCount={Math.min(segments.length, 30)}
      context={{ clientUserId, practitionerUserId, viewerClass, sessionNames }}
      itemContent={renderTranscriptRow}
    />
  );
}
